import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Container,
  PasswordInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { handleSubmitError } from '@utils/forms';

import { SigninRequestBody } from '@interfaces/auth.interface';

import { isApiError } from '@api/index';
import { useRequestActivationMutation } from '@api/users.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';
import useGRecaptcha from '@hooks/useGRecaptcha';

interface LoginFormProps {
  useExternalLinks?: boolean;
  onLoginSuccess?: () => void;
}

export default function LoginForm({
  onLoginSuccess,
  useExternalLinks = false,
}: LoginFormProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const { t } = useTranslation();

  const { signin, isLoading } = useAuth();
  const { getRecaptchaToken } = useGRecaptcha();

  const [params, setParams] = useQueryParams();

  const alert = useMemo(() => {
    const a = params.get('alert');
    return a && a.trim() !== '' ? a : null;
  }, [params]);

  // ==========================================================================
  // State
  // ==========================================================================
  const [message, setMessage] = useState<string | null>(null);
  const [userNotConfirmed, setUserNotConfirmed] = useState(false);

  // ==========================================================================
  // Api
  // ==========================================================================
  const [requestActivation, { isLoading: isLoadingRequestActivation }] =
    useRequestActivationMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
    password: '',
  };

  const form = useForm({
    initialValues,
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : t('login.invalidEmail')),
    },
  });

  const handleRequestActivation = async () => {
    try {
      await requestActivation({ email: form.values.email }).unwrap();

      setUserNotConfirmed(false);
      form.clearErrors();

      setMessage(t('login.activationMessage'));
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const data: SigninRequestBody = {
        email: values.email,
        password: values.password,
      };

      const recaptchaToken = await getRecaptchaToken('SignIn');

      await signin({ body: data, recaptchaToken });

      const redirect = params.get('redirect');
      if (redirect) {
        try {
          const redirectUrl = new URL(redirect, window.location.origin);
          if (redirectUrl.origin === window.location.origin) {
            window.location.href = redirectUrl.href;
            return;
          }
        } catch {
          // Invalid redirect, fall through
        }
      }

      onLoginSuccess?.();
    } catch (e) {
      if (isApiError(e)) {
        if (e.data && e.data.code === 'user-email-not-confirmed') {
          setUserNotConfirmed(true);
        }
      }

      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('login.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('login.noAccount')}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (useExternalLinks) {
              window.open('/account-utente', '_blank');
            } else {
              params.set('p', 'register');
              setParams(params);
            }
          }}
        >
          {t('login.signUp')}
        </Text>
      </Text>

      {alert && (
        <Alert my="md" icon={<IconAlertCircle size={16} />}>
          {alert}
        </Alert>
      )}
      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <TextInput
          type="email"
          label={t('login.email')}
          placeholder={t('login.emailPlaceholder')}
          required
          {...form.getInputProps('email')}
        />
        <PasswordInput
          label={t('login.password')}
          placeholder={t('login.passwordPlaceholder')}
          required
          mt="md"
          {...form.getInputProps('password')}
        />

        <Text
          ta="center"
          mt="md"
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (useExternalLinks) {
              window.open('/account-utente?p=password-reset-request', '_blank');
            } else {
              params.set('p', 'password-reset-request');
              setParams(params);
            }
          }}
        >
          {t('login.forgotPassword')}
        </Text>

        {form.errors.general && (
          <Text c="red" size="sm" ta="center" mt="xl">
            {form.errors.general}
          </Text>
        )}
        {userNotConfirmed && (
          <Button
            mt="sm"
            variant="default"
            fullWidth
            loading={isLoading || isLoadingRequestActivation}
            onClick={handleRequestActivation}
          >
            {t('login.sendVerificationEmail')}
          </Button>
        )}

        {message && (
          <Alert color="green" ta="center" mt="xl">
            {message}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          mt="xl"
          loading={isLoading || isLoadingRequestActivation}
        >
          {t('login.signIn')}
        </Button>
      </form>
    </Container>
  );
}
